/**
 * Lens refraction for the few hero pieces that get it (the home page hero
 * buttons and the navbar "Get Started" over the video).
 *
 * How it works: for each element we draw a displacement map on a canvas at
 * the element's exact size and corner radius. Inside the last `edge` pixels
 * of the shape the map pushes pixels along the edge normal, strongest at the
 * rim; the middle is untouched. The map becomes an SVG <filter> with
 * feImage + feDisplacementMap, and the element's backdrop-filter becomes
 * `blur() url(#filter) saturate()`.
 *
 * Only Chromium browsers (Chrome, Edge, Opera, Samsung Internet) apply an
 * SVG filter inside backdrop-filter. Everyone else keeps the blur-only
 * material, which is what the CSS already paints. The map is rebuilt
 * whenever the element changes size.
 */

const NS = "http://www.w3.org/2000/svg";

let chromium: boolean | null = null;

/** True in Chromium-based browsers, which support url() in backdrop-filter. */
export function isChromium(): boolean {
  if (chromium != null) return chromium;
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { userAgentData?: { brands?: Array<{ brand: string }> } };
  const brands = nav.userAgentData?.brands ?? [];
  const ua = nav.userAgent || "";
  chromium =
    brands.some((b) => /Chromium|Google Chrome|Microsoft Edge|Opera/i.test(b.brand)) ||
    // iOS browsers (CriOS, EdgiOS) are WebKit underneath; Firefox is not Chromium.
    (/Chrome\/\d/.test(ua) && !/CriOS|EdgiOS|FxiOS|Firefox/.test(ua));
  return chromium;
}

let defs: SVGDefsElement | null = null;
let counter = 0;

function ensureDefs(): SVGDefsElement {
  if (defs && defs.isConnected) return defs;
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "0");
  svg.setAttribute("height", "0");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  svg.style.position = "absolute";
  svg.style.width = "0";
  svg.style.height = "0";
  svg.style.overflow = "hidden";
  svg.style.pointerEvents = "none";
  defs = document.createElementNS(NS, "defs");
  svg.appendChild(defs);
  document.body.appendChild(svg);
  return defs;
}

export function newLensId(): string {
  counter += 1;
  return `g-lens-${counter}`;
}

export interface LensShape {
  /** Width of the bending band along the edge, in CSS px. */
  edge: number;
  /** Maximum displacement at the rim, in CSS px. */
  strength: number;
}

/**
 * Build (or rebuild) the refraction filter `id` for `el` at its current size.
 * Returns false when the element is too small to bother.
 */
export function buildLensFilter(el: HTMLElement, id: string, { edge, strength }: LensShape): boolean {
  const w = el.offsetWidth | 0;
  const h = el.offsetHeight | 0;
  if (w < 8 || h < 8) return false;

  const cs = getComputedStyle(el);
  const rad = Math.min(parseFloat(cs.borderTopLeftRadius) || 0, w / 2, h / 2);
  const band = Math.max(2, Math.min(edge, Math.min(w, h) / 2));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  const img = ctx.createImageData(w, h);
  const d = img.data;

  // Signed distance to a rounded rectangle, and its outward normal.
  const hx = w / 2;
  const hy = h / 2;
  const ix = hx - rad;
  const iy = hy - rad;
  for (let y = 0; y < h; y++) {
    const py = y + 0.5 - hy;
    const ay = Math.abs(py);
    const sy = Math.sign(py);
    for (let x = 0; x < w; x++) {
      const px = x + 0.5 - hx;
      const ax = Math.abs(px);
      const sx = Math.sign(px);
      const qx = ax - ix;
      const qy = ay - iy;
      let dist: number;
      let nx: number;
      let ny: number;
      if (qx > 0 && qy > 0) {
        const len = Math.hypot(qx, qy);
        dist = len - rad;
        nx = (qx / len) * sx;
        ny = (qy / len) * sy;
      } else if (qx > qy) {
        dist = qx - rad;
        nx = sx;
        ny = 0;
      } else {
        dist = qy - rad;
        nx = 0;
        ny = sy;
      }
      const inset = -dist;
      let m = 0;
      if (inset >= 0 && inset < band) {
        const t = 1 - inset / band;
        m = t * t;
      }
      const o = (y * w + x) * 4;
      // 128 is "no displacement"; pull samples from inside toward the rim.
      d[o] = 128 - nx * m * 127;
      d[o + 1] = 128 - ny * m * 127;
      d[o + 2] = 128;
      d[o + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const root = ensureDefs();
  document.getElementById(id)?.remove();
  const f = document.createElementNS(NS, "filter");
  f.setAttribute("id", id);
  f.setAttribute("x", "0");
  f.setAttribute("y", "0");
  f.setAttribute("width", String(w));
  f.setAttribute("height", String(h));
  f.setAttribute("filterUnits", "userSpaceOnUse");
  f.setAttribute("primitiveUnits", "userSpaceOnUse");
  f.setAttribute("color-interpolation-filters", "sRGB");
  const fi = document.createElementNS(NS, "feImage");
  fi.setAttribute("href", canvas.toDataURL());
  fi.setAttribute("x", "0");
  fi.setAttribute("y", "0");
  fi.setAttribute("width", String(w));
  fi.setAttribute("height", String(h));
  fi.setAttribute("preserveAspectRatio", "none");
  fi.setAttribute("result", "map");
  const dm = document.createElementNS(NS, "feDisplacementMap");
  dm.setAttribute("in", "SourceGraphic");
  dm.setAttribute("in2", "map");
  dm.setAttribute("scale", String(strength * 2));
  dm.setAttribute("xChannelSelector", "R");
  dm.setAttribute("yChannelSelector", "G");
  f.append(fi, dm);
  root.appendChild(f);
  return true;
}

export function removeLensFilter(id: string): void {
  if (typeof document === "undefined") return;
  document.getElementById(id)?.remove();
}
